import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Star, ShieldCheck, Phone, ArrowRight, Sparkles } from "lucide-react";

const PLANS = [
  {
    key: "basic",
    name: "Basic",
    tagline: "For small shops and individual businesses",
    price: 1499,
    gst: 270,
    total: 1769,
    monthly: 125,
    features: [
      "Sales & purchase billing",
      "Customer & supplier records",
      "Payment & receipt entries",
      "Expense management",
      "Basic inventory",
      "GST invoices",
      "Basic business reports",
      "Data backup",
      "Invoice sharing",
    ],
  },
  {
    key: "professional",
    name: "Professional",
    tagline: "Recommended for growing shops",
    price: 2499,
    gst: 450,
    total: 2949,
    monthly: 208,
    featured: true,
    features: [
      "Everything in Basic",
      "Complete accounting",
      "Advanced inventory & stock management",
      "GST reports (GSTR-1, GSTR-3B)",
      "Outstanding receivable / payable",
      "Profit & Loss statement",
      "Balance Sheet",
      "Multiple invoice formats",
      "Barcode support",
      "WhatsApp invoice sharing",
      "Data export",
      "Automatic backup",
      "Advanced reports",
    ],
  },
  {
    key: "business",
    name: "Business",
    tagline: "For growing businesses & multi-branch",
    price: 3999,
    gst: 720,
    total: 4719,
    monthly: 333,
    features: [
      "Everything in Professional",
      "Multiple users",
      "Multiple businesses / branches",
      "Multiple godowns",
      "User permissions",
      "Stock transfer",
      "Advanced business dashboard",
      "Advanced reports",
      "Priority support",
      "Enhanced data & security features",
    ],
  },
];

const HELPLINE = "8446463026";

const fmt = (n) => `₹${n.toLocaleString("en-IN")}`;

function contactWhatsApp(planName, price) {
  const msg = `Hi ANV Associate, I want to subscribe to the *AccountifyPro ${planName}* plan (${fmt(price)} + GST). Please guide me through activation.`;
  window.open(`https://wa.me/91${HELPLINE}?text=${encodeURIComponent(msg)}`, "_blank");
}

export default function Pricing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="pricing-page">
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => nav("/")} className="text-left" data-testid="pricing-logo">
            <div className="text-[10px] overline">ANV Associate</div>
            <div className="text-xl font-bold tracking-tight text-[#064E3B]">AccountifyPro</div>
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/")}>Home</Button>
            <Button variant="outline" onClick={() => nav("/firm/signup")} data-testid="pricing-signup">Register Firm</Button>
            <Button className="btn-primary" onClick={() => nav("/firm/login")} data-testid="pricing-login">Firm Login</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="overline mb-3 flex items-center justify-center gap-2">
            <Sparkles size={12} className="text-[#064E3B]" /> Simple, transparent pricing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-none">
            Choose the plan that
            <br />
            <span className="text-[#064E3B]">fits your business.</span>
          </h1>
          <p className="mt-6 text-slate-600 max-w-2xl mx-auto">
            Accounting • Billing • Inventory • GST — all included. One-time annual payment,
            no per-user charges on Basic / Professional. Cancel anytime.
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p) => (
            <div
              key={p.key}
              className={`relative rounded-xl border p-6 flex flex-col ${
                p.featured
                  ? "border-[#064E3B] border-2 bg-white shadow-xl md:-translate-y-2"
                  : "border-border bg-white"
              }`}
              data-testid={`plan-card-${p.key}`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#064E3B] text-white text-xs font-bold px-3 py-1 rounded-full">
                  <Star size={12} fill="currentColor" /> RECOMMENDED
                </div>
              )}
              <div className="overline">{p.name}</div>
              <div className="text-sm text-slate-500 mt-1">{p.tagline}</div>

              <div className="mt-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black tracking-tight text-slate-900">{fmt(p.price)}</span>
                  <span className="text-slate-500 text-sm">+ GST</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono">
                  GST {fmt(p.gst)} · Total {fmt(p.total)} / year
                </div>
                <div className="text-xs text-[#064E3B] font-semibold mt-1">
                  ≈ {fmt(p.monthly)} / month
                </div>
              </div>

              <ul className="mt-6 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check size={14} className="text-[#166534] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 space-y-2">
                <Button
                  className={p.featured ? "btn-primary w-full" : "w-full"}
                  variant={p.featured ? "default" : "outline"}
                  onClick={() => contactWhatsApp(p.name, p.price)}
                  data-testid={`plan-buy-${p.key}`}
                >
                  Get {p.name} <ArrowRight size={14} className="ml-1" />
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-xs"
                  onClick={() => nav("/firm/signup")}
                  data-testid={`plan-trial-${p.key}`}
                >
                  Start free trial
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison note */}
        <div className="mt-10 text-center text-xs text-muted-foreground">
          * Monthly equivalent shown is the pre-GST annual price ÷ 12. All plans include free updates
          and free WhatsApp click-to-chat support.
        </div>

        {/* Trust / helpline strip */}
        <div className="mt-14 bg-white border border-border rounded-xl p-6 md:p-8 grid md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="text-[#064E3B] flex-shrink-0" />
            <div>
              <div className="font-semibold">GST-Ready. Audit-Ready.</div>
              <div className="text-xs text-slate-500 mt-1">
                GSTR-1, GSTR-3B, Balance Sheet, P&amp;L — export any report to Excel / PDF.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Sparkles className="text-[#064E3B] flex-shrink-0" />
            <div>
              <div className="font-semibold">Free WhatsApp reminders</div>
              <div className="text-xs text-slate-500 mt-1">
                Drafts pending-payment nudges via <span className="font-mono">wa.me</span> — no Twilio, no per-message fee.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="text-[#064E3B] flex-shrink-0" />
            <div>
              <div className="font-semibold">Talk to us — {HELPLINE}</div>
              <div className="text-xs text-slate-500 mt-1">
                Mon–Sat 10:00–19:00 IST. WhatsApp or call the same number.
              </div>
              <Button
                variant="link"
                className="px-0 h-auto text-[#064E3B]"
                onClick={() => window.open(`https://wa.me/91${HELPLINE}?text=${encodeURIComponent("Hi, I have a question about AccountifyPro plans")}`)}
                data-testid="pricing-whatsapp-help"
              >
                Chat on WhatsApp →
              </Button>
            </div>
          </div>
        </div>

        {/* FAQ mini */}
        <div className="mt-14 max-w-3xl mx-auto">
          <h2 className="text-lg font-bold mb-4">Frequently asked</h2>
          <div className="space-y-3 text-sm">
            {[
              { q: "Do you offer a free trial?", a: "Yes — sign up and use every feature free during the trial period. No card required." },
              { q: "Can I upgrade my plan later?", a: "Absolutely. Contact us on WhatsApp and we'll pro-rate the difference for the remaining months." },
              { q: "Is my data safe?", a: "Yes. Automatic backup runs daily and all data is encrypted at rest. You can export raw Excel any time." },
              { q: "Do plans include GST filing?", a: "Plans include GST-compliant invoices + GSTR-1 / 3B reports for filing. Actual filing on the GST portal is done by your CA." },
            ].map((f) => (
              <details key={f.q} className="bg-white border border-border rounded-md p-3">
                <summary className="cursor-pointer font-semibold text-slate-800">{f.q}</summary>
                <div className="mt-2 text-slate-600">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-border bg-white py-4 mt-14">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} AccountifyPro • Developed by <span className="font-semibold text-slate-700">ANV ASSOCIATE</span></div>
          <div className="flex items-center gap-1"><Phone size={12} /> {HELPLINE}</div>
        </div>
      </footer>
    </div>
  );
}
